import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Money } from "@cliqsuite/ui";
import { receiptUrl } from "../lib/format";
import { printReceipt } from "../lib/print";
import type { ReceiptMode } from "../hooks/useStoreConfig";

// Paid (canvas v2-d-done*, v2-m-done): coral wipe, the amount, and a receipt QR, with paper as the
// store has set it.

const AUTO_CLOSE_MS = 5000;

export interface DoneScreenProps {
  isDesktop: boolean;
  orderId: string;
  orderNo: string;
  total: number;
  tenderLabel: string;
  earnedPoints: number;
  memberName: string | null;
  /** Show the QR even for a member sale. */
  receiptOnScreen?: boolean;
  /** Saved on this till because the server couldn't be reached; it syncs by itself. */
  pendingSync: boolean;
  currency: { code: string; precision: 0 | 2 | 3 };
  receiptMode: ReceiptMode;
  onNewOrder: () => void;
}

export function DoneScreen(p: DoneScreenProps) {
  const url = receiptUrl(p.orderId);
  const canvas = useRef<HTMLCanvasElement>(null);
  // Tapping the receipt keeps it up while the customer scans.
  const [held, setHeld] = useState(false);
  const [printed, setPrinted] = useState(false);
  // An offline sale isn't on the server yet, so there's nothing to print from.
  const canPrint = !p.pendingSync;
  const print = () => {
    setPrinted(true);
    void printReceipt(p.orderId);
  };
  useEffect(() => {
    if (p.receiptMode === "print" && canPrint) print();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (url && canvas.current) void QRCode.toCanvas(canvas.current, url, { width: 190, margin: 1, color: { dark: "#20242E" } });
  }, [url]);

  useEffect(() => {
    if (held) return;
    const t = setTimeout(p.onNewOrder, AUTO_CLOSE_MS + 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held]);

  const firstName = p.memberName?.split(" ")[0] ?? "";

  return (
    <div className={`pos-done${p.isDesktop ? "" : " phone"}`} role="status" aria-live="polite">
      <div className="pos-done-main">
        <div className="pos-done-tick">
          <svg aria-hidden="true" width="44" height="44" viewBox="0 0 24 24">
            <polyline points="5 12.5 10 17.5 19 7.5" fill="none" stroke="#DC2D49" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="pos-done-by">
          Paid by {/^(cash|card)$/i.test(p.tenderLabel) ? p.tenderLabel.toLowerCase() : p.tenderLabel}
          {p.orderNo && p.orderNo !== "—" ? ` · #${p.orderNo}` : ""}
        </div>
        <div className="pos-done-amount"><Money amount={p.total} opts={{ currencyCode: p.currency.code, precision: p.currency.precision }} /></div>
        {p.pendingSync ? (
          <div className="pos-done-note offline">
            <span className="ms" aria-hidden="true">sync</span>
            Couldn't reach the server. Saved on this register, it syncs by itself once you're back online.
          </div>
        ) : (
          p.earnedPoints > 0 &&
          p.memberName && (
            <div className="pos-done-note points">
              <span className="ms" aria-hidden="true">loyalty</span>+{p.earnedPoints} points to {p.memberName}
            </div>
          )
        )}
        <div className="pos-done-actions">
          <button onClick={p.onNewOrder}>
            <span className="ms" aria-hidden="true">add</span>
            New order
          </button>
        </div>
        <div className={`pos-done-timer${held ? " paused" : ""}`} style={{ ["--pos-done-ms" as string]: `${AUTO_CLOSE_MS}ms` }}>
          <div>
            <div />
          </div>
          <small>{held ? "Kept open while they scan. Tap New order when done." : "A fresh ticket opens on its own in 5 s."}</small>
        </div>
      </div>

      <div className="pos-receipt-card">
      <button className="pos-receipt-hold" onClick={() => setHeld(true)} aria-label="Keep the receipt on screen">
        {p.memberName && !p.receiptOnScreen ? (
          <>
            <div className="pos-receipt-icon">
              <span className="ms" aria-hidden="true" style={{ fontSize: 28, color: "var(--coral)" }}>
                smartphone
              </span>
            </div>
            <b>Sent to {firstName}'s app</b>
            <p>
              The receipt{p.earnedPoints > 0 ? ` and ${p.earnedPoints} new points are` : " is"} in their CliqSuite app, under Receipts.
            </p>
          </>
        ) : (
          <>
            <small>Your receipt</small>
            {url ? <canvas ref={canvas} width={190} height={190} aria-label="QR code for the receipt" /> : null}
            <b>Scan for your receipt</b>
            <p>{url ? (p.receiptMode === "digital" ? "No paper. Works on any phone camera." : "Works on any phone camera.") : "Receipt links aren't set up on this register yet."}</p>
          </>
        )}
        <div className="pos-receipt-code">
          <span>Receipt</span>
          <span className="num">{p.orderNo && p.orderNo !== "—" ? `#${p.orderNo}` : p.orderId.slice(0, 8).toUpperCase()}</span>
        </div>
      </button>
      {p.receiptMode !== "digital" && canPrint && (
        <button className="pos-receipt-print" onClick={() => { setHeld(true); print(); }} disabled={printed && p.receiptMode === "ask"}>
          <span className="ms" aria-hidden="true">print</span>
          {!printed ? "Print receipt" : p.receiptMode === "ask" ? "Sent to printer" : "Print again"}
        </button>
      )}
      </div>
    </div>
  );
}
